/**
 * useAIInsights — TanStack Query hook для AI-агроном panel.
 *
 * TODO(backend): endpoint `/api/v1/dashboard/insights?date={date}` ещё не
 * реализован (NDVI-6 в roadmap). Пока используем мок: пытаемся запросить,
 * на 404 возвращаем mock-data чтобы UI работал. Когда backend готов — мок
 * автоматически отвалится.
 */
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';

export interface AICriticalField {
  id: string;
  name: string;
  ndvi: number;
  reason: string;
}

export interface AIInsights {
  good: number;
  warning: number;
  bad: number;
  critical_fields: AICriticalField[];
  /** Генерированный narrative ("23 поля растут хорошо. 5 замедлились — стоит проверить полив."). */
  narrative?: string;
}

const MOCK: AIInsights = {
  good: 23,
  warning: 5,
  bad: 2,
  critical_fields: [
    {
      id: 'mock-f1',
      name: 'Поле №14',
      ndvi: 0.32,
      reason: 'NDVI упал на 0.18 за неделю — возможна засуха',
    },
    {
      id: 'mock-f2',
      name: 'Поле №7',
      ndvi: 0.28,
      reason: 'Низкая однородность (CV 0.34) — проверьте всходы',
    },
  ],
  narrative:
    '23 поля растут стабильно. 5 замедлились — рекомендуем проверить полив. 2 в критическом состоянии (см. список ниже).',
};

// Backend response shape (commit 63e0bc60 — backend/src/ikolhoz/api/v1/dashboard.py).
// Backend возвращает CriticalField с ndvi_latest/ndvi_prev/ndvi_delta + status + farm_name.
// Frontend AICriticalField ждёт flat {id, name, ndvi, reason} — нормализуем здесь.
interface BackendCriticalField {
  id: string;
  name: string;
  farm_name?: string | null;
  area_ha?: number;
  ndvi_latest?: number | null;
  ndvi_latest_date?: string | null;
  ndvi_prev?: number | null;
  ndvi_delta?: number | null;
  status?: 'good' | 'warning' | 'bad' | 'unknown';
  reason?: string;
}

interface BackendInsightsResponse {
  target_date?: string;
  good?: number;
  warning?: number;
  bad?: number;
  unknown?: number;
  total?: number;
  critical_fields?: BackendCriticalField[];
}

function normalizeBackendInsights(resp: unknown): AIInsights {
  // Defensive: принимаем любой shape, выдаём гарантированно-валидный AIInsights.
  // Backend (commit 63e0bc60) возвращает {good, warning, bad, critical_fields[]}
  // где каждое critical_field имеет ndvi_latest (не ndvi).
  const r = (resp ?? {}) as Record<string, unknown>;
  const rawFields = Array.isArray(r.critical_fields) ? r.critical_fields : [];
  return {
    good: typeof r.good === 'number' ? r.good : 0,
    warning: typeof r.warning === 'number' ? r.warning : 0,
    bad: typeof r.bad === 'number' ? r.bad : 0,
    narrative: typeof r.narrative === 'string' ? r.narrative : undefined,
    critical_fields: rawFields.map((rawF) => {
      const f = (rawF ?? {}) as Record<string, unknown>;
      // Берём ndvi_latest (backend) → ndvi (mock) → 0 (fallback)
      const ndviRaw = (f.ndvi_latest ?? f.ndvi) as unknown;
      const ndvi = typeof ndviRaw === 'number' ? ndviRaw : 0;
      return {
        id: typeof f.id === 'string' ? f.id : '',
        name: typeof f.name === 'string' ? f.name : 'Без названия',
        ndvi,
        reason: typeof f.reason === 'string' ? f.reason : '—',
      };
    }),
  };
}

export function useAIInsights(selectedDate: string) {
  return useQuery<AIInsights, Error>({
    queryKey: ['ai-insights', selectedDate],
    queryFn: async () => {
      try {
        // Backend ждёт `target_date`, не `date` (commit 63e0bc60).
        const raw = await api.get<BackendInsightsResponse>(
          `/api/v1/dashboard/insights?target_date=${selectedDate}`,
        );
        return normalizeBackendInsights(raw);
      } catch (err) {
        if (err instanceof ApiError && (err.status === 404 || err.status === 501)) {
          return MOCK;
        }
        throw err;
      }
    },
    staleTime: 5 * 60_000,
    retry: 1,
  });
}
